import { Check, Crown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { site } from "@/data/site";

function TierCard({ tier, index }) {
  const mailHref = `mailto:${site.email}?subject=${encodeURIComponent(
    `${tier.tag} — ${tier.name} Sign-Up`
  )}`;
  const hot = tier.highlight;

  return (
    <Reveal
      delay={index * 0.12}
      className={hot ? "lg:-translate-y-6 lg:scale-[1.03] lg:z-10" : ""}
    >
      <article
        className={`relative flex h-full flex-col border-2 p-8 lg:p-10 ${
          hot
            ? "border-purple bg-[#140A1F] shadow-[0_0_45px_rgba(168,50,255,0.35)]"
            : "border-white/12 bg-surface"
        }`}
        data-testid={`tier-card-${tier.id}`}
      >
        {hot && (
          <div className="clip-tag absolute -top-4 left-8 flex items-center gap-2 bg-gold px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-black" data-testid="tier-2-badge">
            <Crown className="h-3.5 w-3.5" />
            Main Tier — Most Drivers Start Here
          </div>
        )}

        <p className={`text-xs font-bold uppercase tracking-[0.3em] ${hot ? "text-purple" : "text-gold"}`}>
          {tier.tag}
        </p>
        <h3 className="mt-3 font-display text-2xl font-black uppercase leading-tight tracking-tight text-white">
          {tier.name}
        </h3>

        <div className="mt-6 flex items-end gap-2">
          <span className={`font-display text-5xl font-black tracking-tighter ${hot ? "text-purple" : "text-white"}`} data-testid={`tier-price-${tier.id}`}>
            ${tier.price}
          </span>
          <span className="pb-1 text-xs font-bold uppercase tracking-[0.2em] text-white/45">
            / month
          </span>
        </div>

        <p className="mt-5 border-l-2 border-gold/60 pl-4 text-sm font-semibold leading-relaxed text-white/70">
          {tier.blurb}
        </p>

        <ul className="mt-8 flex flex-col gap-4">
          {tier.features.map((feature, i) => (
            <li key={i} className="flex items-start gap-3">
              <Check className={`mt-0.5 h-4 w-4 shrink-0 ${hot ? "text-purple" : "text-gold"}`} strokeWidth={3} />
              <span className="text-sm font-medium leading-relaxed text-white/75">{feature}</span>
            </li>
          ))}
        </ul>

        <p className="mt-8 text-xs font-semibold uppercase tracking-[0.12em] leading-relaxed text-white/40">
          <span className={hot ? "text-purple" : "text-gold"}>Ideal for: </span>
          {tier.ideal}
        </p>

        <a
          href={mailHref}
          className={`mt-10 block border-2 px-6 py-4 text-center text-sm font-black uppercase tracking-[0.15em] transition-all duration-200 hover:-translate-y-1 ${
            hot
              ? "border-purple bg-purple text-white hover:bg-transparent hover:shadow-[0_0_25px_rgba(168,50,255,0.6)]"
              : "border-gold bg-transparent text-gold hover:bg-gold hover:text-black hover:shadow-[0_0_25px_rgba(255,215,0,0.4)]"
          }`}
          data-testid={`tier-cta-${tier.id}`}
        >
          Lock In {tier.tag.split(" ")[0]} {tier.tag.split(" ")[1]}
        </a>
      </article>
    </Reveal>
  );
}

export default function Tiers() {
  return (
    <section id="programs" className="relative py-28 lg:py-40" data-testid="tiers-section">
      <div className="halftone pointer-events-none absolute inset-0 opacity-40" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <Reveal>
              <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.3em] text-gold">
                <span className="h-px w-10 bg-gold" />
                Coaching Programs
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-5xl lg:text-6xl">
                Pick your <span className="text-stroke-purple">pace.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="max-w-sm">
            <p className="text-sm font-medium leading-relaxed text-white/60">
              Three levels of coaching access. Every tier runs through the
              training app with full progress tracking — the difference is how
              deep we go together.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-8 lg:mt-24 lg:grid-cols-3 lg:gap-6">
          {site.tiers.map((tier, i) => (
            <TierCard key={tier.id} tier={tier} index={i} />
          ))}
        </div>

        <Reveal delay={0.2}>
          <p className="mt-12 text-center text-xs font-bold uppercase tracking-[0.25em] text-white/40">
            Not sure which tier fits? DM{" "}
            <a
              href={site.igUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold underline-offset-4 hover:underline"
              data-testid="tiers-help-link"
            >
              {site.igHandle}
            </a>{" "}
            and we'll figure it out.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
